<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import { ROUTES } from '@/router/routes'
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
  } from '@/components/ui/select'
  import {
    Loader2,
    CheckCircle2,
    AlertCircle,
    Trash2,
    ExternalLinkIcon,
    RefreshCcw
  } from 'lucide-vue-next'
  import {
    FormsService,
    OrganisationsService,
    type OrganisationsListResponse,
    type TForm,
    type TFormRequest
  } from '@/types/generated'
  import { createSuccessToast } from '@/lib/toasts'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  type TFormWithStatus = TForm & {
    status: 'uploading' | 'success' | 'error'
    originalSize?: number
  }

  const { t } = useTranslation({
    en: {
      uploadDocuments: 'Upload Documents',
      customer: 'Customer',
      dropFilesHere: 'Drop your file here or ',
      allowedExtensions: 'Allowed file extensions: pdf',
      documentName: 'Document name',
      description: 'Description',
      size: 'Size',
      status: 'Status',
      saveDocuments: 'Save documents',
      browse: 'Browse'
    },
    de: {
      uploadDocuments: 'Dokumente hochladen',
      dropFilesHere: 'Dateien hier ablegen oder ',
      allowedExtensions: 'Erlaubte Dateierweiterungen: pdf',
      documentName: 'Dokumentname',
      description: 'Beschreibung',
      customer: 'Kunde',
      size: 'Größe',
      status: 'Status',
      saveDocuments: 'Dokumente speichern',
      browse: 'Durchsuchen'
    }
  })

  onMounted(async () => {
    await fetchCustomers()
  })

  const customersList = ref<OrganisationsListResponse>()
  const selectedCustomer = ref('')
  const uploadedFiles = ref<TFormWithStatus[]>([])
  const isDragging = ref(false)
  const isLoading = ref(true)

  const fetchCustomers = async () => {
    try {
      const resp = await OrganisationsService.organisationsList({ limit: 0, offset: 0 })
      customersList.value = resp
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      isLoading.value = false
    }
  }

  const customerOptions = computed<Option[]>(() => {
    return (
      customersList.value?.results.map((customer) => ({
        value: customer.uuid ?? '',
        title: customer.name ?? ''
      })) ?? []
    )
  })

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = true
  }

  const handleDragLeave = () => {
    isDragging.value = false
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = false
    handleFileUpload(event.dataTransfer?.files || null)
  }

  const updateDocument = async (updatedDoc: TFormWithStatus) => {
    const formData: TFormRequest = {
      title: updatedDoc.title,
      description: updatedDoc.description,
      organisation_uuid: selectedCustomer.value
    }
    try {
      await FormsService.formsPartialUpdate({ id: updatedDoc.id, formData })
      createSuccessToast()
    } catch (error) {
      console.error('Error updating document:', error)
    }
  }

  const retryUpload = async (document: TFormWithStatus) => {
    document.status = 'uploading'
    if (!document.file || typeof document.file !== 'object') {
      console.error('Invalid file object')
      document.status = 'error'
      return
    }
    const formData: TFormRequest = {
      title: document.title,
      organisation_uuid: selectedCustomer.value,
      file: document.file,
      description: document.description || ''
    }
    try {
      const createdDocument = await FormsService.formsCreate({ formData })
      const index = uploadedFiles.value.findIndex((f) => f.id === document.id)
      if (index !== -1) {
        uploadedFiles.value[index] = { ...createdDocument, status: 'success' } as TFormWithStatus
      }
      createSuccessToast()
    } catch (error) {
      console.error('Error retrying upload:', error)
      document.status = 'error'
    }
  }

  const deleteDocument = async (document: TFormWithStatus) => {
    try {
      await FormsService.formsDestroy({ id: document.id })
      uploadedFiles.value = uploadedFiles.value.filter((doc) => doc.id !== document.id)
      createSuccessToast()
    } catch (error) {
      console.error('Error deleting document:', error)
    }
  }
  const getCustomerInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
  }

  const getCustomerName = (uuid: string) => {
    return customersList.value?.results.find((c) => c.uuid === uuid)?.name ?? ''
  }

  const handleFileUpload = async (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files)

      for (const file of newFiles) {
        const newFile: TFormWithStatus = {
          id: Date.now().toString(), // Temporary unique ID
          title: file.name,
          description: '',
          status: 'uploading',
          file: file,
          originalSize: file.size
        }
        uploadedFiles.value.push(newFile)

        const formData: TFormRequest = {
          title: file.name,
          organisation_uuid: selectedCustomer.value,
          file: file,
          description: ''
        }

        try {
          const createdDocument = await FormsService.formsCreate({ formData })
          const index = uploadedFiles.value.findIndex((f) => f.id === newFile.id)
          if (index !== -1) {
            uploadedFiles.value[index] = {
              ...createdDocument,
              status: 'success',
              originalSize: newFile.originalSize
            } as TFormWithStatus
          }
          createSuccessToast()
        } catch (error) {
          console.error('Error uploading file:', error)
          const index = uploadedFiles.value.findIndex((f) => f.id === newFile.id)
          if (index !== -1) {
            uploadedFiles.value[index].status = 'error'
          }
        }
      }
    }
  }

  const openFile = (file: TFormWithStatus) => {
    if (typeof file.file === 'string') {
      window.open(file.file, '_blank')
    }
  }

  const fileInput = ref<HTMLInputElement | null>(null)

  const openFilePicker = () => {
    fileInput.value?.click()
  }

  watch(selectedCustomer, () => {
    uploadedFiles.value = []
  })
</script>

<template>
  <div class="min-h-screen bg-gray-100 p-2 sm:p-5 sm:py-0 md:pt-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Documents', to: ROUTES.ADMIN.DOCUMENTS.DOCUMENTS_LIST },
          { title: 'Create Document', to: ROUTES.ADMIN.DOCUMENTS.DOCUMENT_CREATE }
        ]"
      />
      <div class="flex items-center justify-end gap-x-2"></div>
    </div>
    <div
      class="mt-4 flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
        dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div class="mb-6 rounded-lg">
        <div class="mb-4 w-[600px]">
          <SelectField
            v-model="selectedCustomer"
            :label="t('Customer')"
            :is-filterable="true"
            :options="customerOptions"
            :name="`selectedOrganization`"
          />
        </div>

        <div
          v-if="selectedCustomer"
          :class="[
            'mb-4 w-[600px] rounded-lg border-2 border-dashed p-20 text-center',
            isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
          ]"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
          @drop="handleDrop"
        >
          <span>{{ t('dropFilesHere') }}</span>
          <span
            class="cursor-pointer text-purple-600 hover:underline"
            @click="openFilePicker"
          >
            {{ t('browse') }}
          </span>
          <p class="mt-1 text-sm text-gray-500">{{ t('allowedExtensions') }}</p>
          <input
            id="file-upload"
            ref="fileInput"
            type="file"
            multiple
            accept=".pdf"
            class="sr-only"
            @change="(e) => handleFileUpload((e.target as HTMLInputElement)?.files ?? null)"
          />
        </div>
        <div
          v-if="uploadedFiles.length > 0"
          class="overflow-hidden rounded-lg bg-white shadow-sm"
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{{ t('documentName') }}</TableHead>
                <TableHead>{{ t('description') }}</TableHead>
                <TableHead>{{ t('customer') }}</TableHead>
                <TableHead>{{ t('size') }}</TableHead>
                <TableHead>{{ t('status') }}</TableHead>
                <TableHead class="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="file in uploadedFiles"
                :key="file.id"
              >
                <TableCell>
                  <div class="flex items-center space-x-2">
                    <Input
                      v-model="file.title"
                      class="w-full"
                      :disabled="file.status !== 'success'"
                      @blur="updateDocument(file)"
                    />
                    <button
                      v-if="file.status === 'success'"
                      class=""
                      @click="openFile(file)"
                    >
                      <ExternalLinkIcon class="h-6 w-6" />
                    </button>
                  </div>
                </TableCell>
                <TableCell>
                  <Input
                    v-model="file.description"
                    :placeholder="t('description')"
                    class="w-full"
                    :disabled="file.status !== 'success'"
                    @blur="updateDocument(file)"
                  />
                </TableCell>
                <TableCell>
                  <div class="flex items-center">
                    <div class="h-10 w-10 flex-shrink-0">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500"
                      >
                        <span class="text-sm font-medium text-white">
                          {{ getCustomerInitials(getCustomerName(selectedCustomer)) }}
                        </span>
                      </div>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ getCustomerName(selectedCustomer) }}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {{
                    file.originalSize ? `${(file.originalSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'
                  }}
                </TableCell>
                <TableCell>
                  <div
                    v-if="file.status === 'uploading'"
                    class="flex items-center text-blue-500"
                  >
                    <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                    Uploading
                  </div>
                  <div
                    v-else-if="file.status === 'success'"
                    class="flex items-center text-green-500"
                  >
                    <CheckCircle2 class="mr-2 h-4 w-4 text-green-500" />
                    Success
                  </div>
                  <button
                    v-else-if="file.status === 'error'"
                    class="flex items-center text-red-500 hover:text-red-600"
                    type="button"
                    @click="retryUpload(file)"
                  >
                    <RefreshCcw class="mr-2 h-4 w-4" />
                    Retry
                  </button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    :disabled="file.status !== 'success'"
                    @click="deleteDocument(file)"
                  >
                    <Trash2 class="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  </div>
</template>
